import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { credentialsService } from "@/api/credentials";
import { useTranslation } from "@/hooks/useTranslation";

const CredentialsSettings = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        companyName: "",
        address: "",
        phoneNumber: "",
        taxId: ""
    });

    // Fetch current credentials
    const {
        data: credentials,
        isLoading,
        error
    } = useQuery({
        queryKey: ["credentials"],
        queryFn: () => credentialsService.getCredentials(),
        retry: false,
    });

    // Update credentials mutation
    const updateMutation = useMutation({
        mutationFn: (data) => credentialsService.updateCredentials(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["credentials"]);
            alert(t("companySettings.updateSuccess"));
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("companySettings.updateError"));
        },
    });

    // Initialize form when credentials are loaded
    useEffect(() => {
        if (credentials) {
            setFormData({
                companyName: credentials.companyName || "",
                address: credentials.address || "",
                phoneNumber: credentials.phoneNumber || "",
                taxId: credentials.taxId || ""
            });
        }
    }, [credentials]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateMutation.mutate(formData);
    };

    const handleReset = () => {
        if (credentials) {
            setFormData({
                companyName: credentials.companyName || "",
                address: credentials.address || "",
                phoneNumber: credentials.phoneNumber || "",
                taxId: credentials.taxId || ""
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <div className="p-6">
            <TaleDashbaordMainCard className="p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold">{t("companySettings.title")}</h1>
                    <p className="text-gray-600">
                        {t("companySettings.subtitle")}
                    </p>
                    {credentials?.timestamp && (
                        <span className="block text-sm text-gray-500 mt-1">
                            {t("companySettings.lastUpdated", { date: new Date(credentials.timestamp).toLocaleDateString('fr-FR') })}
                        </span>
                    )}
                    <hr />
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Company Information */}
                        <div className="space-y-4">
                            <TaleInput
                                label={t("companySettings.companyNameLabel")}
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleInputChange}
                                required
                                placeholder={t("companySettings.companyNamePlaceholder")}
                                maxLength={100}
                            />

                            <TaleInput
                                label={t("companySettings.taxIdLabel")}
                                name="taxId"
                                value={formData.taxId}
                                onChange={handleInputChange}
                                required
                                placeholder={t("companySettings.taxIdPlaceholder")}
                                maxLength={20}
                                helperText={t("companySettings.taxIdHelper")}
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="space-y-4">
                            <TaleInput
                                label={t("companySettings.addressLabel")}
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                placeholder={t("companySettings.addressPlaceholder")}
                                maxLength={200}
                            />

                            <TaleInput
                                label={t("companySettings.phoneLabel")}
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                required
                                placeholder={t("companySettings.phonePlaceholder")}
                                maxLength={20}
                                helperText={t("companySettings.phoneHelper")}
                            />
                        </div>
                    </div>

                    {/* Preview Section */}
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                        <h3 className="text-lg font-semibold mb-4">{t("companySettings.previewTitle")}</h3>
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900">
                                        {formData.companyName || t("companySettings.previewCompanyNamePlaceholder")}
                                    </h4>
                                    <p className="text-gray-600 mt-1">
                                        {formData.address || t("companySettings.previewAddressPlaceholder")}
                                    </p>
                                    <div className="mt-2 text-sm text-gray-500">
                                        <p>{t("companySettings.previewPhone", { phone: formData.phoneNumber || t("companySettings.previewPhonePlaceholder") })}</p>
                                        <p>{t("companySettings.previewTaxId", { taxId: formData.taxId || t("companySettings.previewTaxIdPlaceholder") })}</p>
                                    </div>
                                </div>
                                <div className="text-right rtl:text-left">
                                    <div className="text-lg font-bold text-blue-600">{t("companySettings.invoiceHeaderWord")}</div>
                                    <div className="text-sm text-gray-500 mt-1">#FAC-2024-001</div>
                                    <div className="text-xs text-gray-400 mt-1">{t("companySettings.legislationCompliant")}</div>
                                </div>
                            </div>

                            <div className="border-t pt-4 text-sm text-gray-500">
                                <p>{t("companySettings.appearsInHeaderNote")}</p>
                                <p className="mt-1 text-xs text-gray-400">
                                    <i className="fas fa-info-circle mr-1 rtl:mr-0 rtl:ml-1"></i>
                                    {t("companySettings.vatComplianceNote")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Usage Information */}
                    <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <h3 className="text-lg font-semibold mb-3">{t("companySettings.whereUsedTitle")}</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-start">
                                    <i className="fas fa-file-invoice text-blue-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.whereUsed1")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-download text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.whereUsed2")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-envelope text-purple-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.whereUsed3")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-print text-orange-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.whereUsed4")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-balance-scale text-red-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.whereUsed5")}
                                </li>
                            </ul>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <h3 className="text-lg font-semibold mb-3">{t("companySettings.tipsTitle")}</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.tip1")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.tip2")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.tip3")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.tip4")}
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 rtl:mr-0 rtl:ml-2 mt-1"></i>
                                    {t("companySettings.tip5")}
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-4 border-t">
                        <div className="text-sm text-gray-600">
                            <p className="font-medium">{t("companySettings.importantInfoTitle")}</p>
                            <ul className="list-disc pl-5 rtl:pl-0 rtl:pr-5 mt-1 space-y-1">
                                <li>{t("companySettings.importantInfo1")}</li>
                                <li>{t("companySettings.importantInfo2")}</li>
                                <li>{t("companySettings.importantInfo3")}</li>
                                <li>{t("companySettings.importantInfo4")}</li>
                            </ul>
                        </div>

                        <div className="flex gap-4">
                            <TaleButton
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                disabled={updateMutation.isPending || !credentials}
                            >
                                <i className="fas fa-undo mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("companySettings.resetButton")}
                            </TaleButton>
                            <TaleButton
                                type="submit"
                                loading={updateMutation.isPending}
                            >
                                <i className="fas fa-save mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("companySettings.saveChangesButton")}
                            </TaleButton>
                        </div>
                    </div>
                </form>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default CredentialsSettings;
